// HostDashboard.js
import React, { useEffect, useMemo, useState } from "react";
import {useSelector} from "react-redux";
import {
  PlusCircle, Search, Filter, Link2, Copy, Check, ExternalLink, MoreVertical,
  User, Home, Phone, FileText, Calendar, Download, AlertCircle,
  CheckCircle2, X, Sparkles, BadgeCheck, Image as ImageIcon
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import request from "../utils/request";



/* --------------- Design tokens --------------- */
const COLORS = {
  bg: "#F8F8F8",
  surface: "#FFFFFF",
  text: "#222222",
  subtext: "#717171",
  border: "#E5E5E5",
  accent: "#FF385C",
  focus: "#FFB3C1",
  success: "#22C55E",
  warn: "#F59E0B",
  info: "#3B82F6",
  muted: "#F4F4F5",
};

/* --------------- Status model --------------- */
const STATUSES = [
  { key: "active",        label: "Active",        chipBg: "#FFEDEB", chipText: "#B4232C", dot: "#FF385C" },
  { key: "awaiting_info", label: "Awaiting Info", chipBg: "#FFF7E6", chipText: "#A16207", dot: "#F59E0B" },
  { key: "scheduled",     label: "Scheduled",     chipBg: "#E6F2FF", chipText: "#1D4ED8", dot: "#3B82F6" },
  { key: "completed",     label: "Completed",     chipBg: "#E9FBEF", chipText: "#0B8A3B", dot: "#22C55E" },
  { key: "archived",      label: "Archived",      chipBg: "#F4F4F5", chipText: "#3F3F46", dot: "#A1A1AA" },
];

const mapStatusToUi = (s) => (s === "created" ? "active" : s);

/* --------------- Image sources (optional) --------------- */
const HERO_IMAGES = ["https://bellhopt-public.s3.us-west-1.amazonaws.com/banner.png"];
const THUMBNAILS  = ["https://bellhopt-public.s3.us-west-1.amazonaws.com/villadefault.png"];

/* --------------- Helpers --------------- */
const cn = (...arr) => arr.filter(Boolean).join(" ");
const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

/* --------------- Smart image with graceful fallback --------------- */
function SmartImage({ sources = [], alt = "", className = "", style = {} }) {
  const [index, setIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const src = sources[index];

  return (
    <div className={cn("relative", className)} style={style}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center"
             style={{ background: "linear-gradient(135deg,#FFE4EA,#FFF5F7,#FFFFFF)", color: "#FF385C" }}>
          <ImageIcon className="w-8 h-8 opacity-80" />
        </div>
      )}
      {src && (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          onLoad={() => setLoaded(true)}
          onError={() => setIndex(i => (i < sources.length - 1 ? i + 1 : i))}
        />
      )}
    </div>
  );
}


/* --------------- Tiny toast --------------- */
function Toast({ show, children }) {
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 12 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full shadow"
          style={{ backgroundColor: COLORS.text, color: "#FFFFFF", zIndex: 60 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function StatusChip({ status, compact = false }) {
  const s = STATUSES.find((x) => x.key === status) || STATUSES[0];
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium whitespace-nowrap ${compact ? "text-[11px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`}
      style={{ backgroundColor: s.chipBg, color: s.chipText, maxWidth: compact ? 120 : "100%" }}
      title={s.label}
    >
      <span className="w-1.5 h-1.5 rounded-full mr-1.5 shrink-0" style={{ backgroundColor: s.dot }} />
      <span className="truncate">{s.label}</span>
    </span>
  );
}

/* --------------- Main --------------- */
function ConciergeDashboard() {


const {hostInfo: hostLogin} = useSelector((state) => state.host);
  // Data
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [query, setQuery]             = useState("");
  const [statusTab, setStatusTab]     = useState("active");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [dateRange, setDateRange]     = useState({ from: "", to: "" });
  const [copiedId, setCopiedId]       = useState(null);
  const [toast, setToast]             = useState("");

  // Views
  const [view, setView] = useState("cards"); // "cards" or "table"

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [form, setForm]             = useState({
    guestName: "",
    guestEmail: "",
    address: "",
    arrivalDate: "",
    arrivalTime: "",
    hostName: "",
    hostContact: "",
    otaHostEmail: "",
    specialRequests: "",
    services: [],
    budgetUSD: "",
    notes: "",
    tags: [], // optional UI
  });

  // Details drawer
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [drawerOrder, setDrawerOrder] = useState(null);

  // IMPORTANT: replace with your pre-created Property ObjectId (not uniqueId)
  const DEFAULT_PROPERTY_ID = "68a811ee6ffae1d09f6d4773";

  // Fetch (optional list; falls back to demo if 404)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await request.get("/ota/orders");
        if (mounted) setOrders((data?.orders || []).map(mapApiOrderToUi));
      } catch {
        if (mounted) {
          setOrders([
            {
              id: "ord_1001", guestName: "Sophia Williams",
              address: "28202 Cabot Rd Ste 391, Laguna Beach, CA",
              hostName: "Alexander Ali", hostContact: "+1 310 555 1212",
              otaHostEmail: "host@example.com", guestEmail: "guest@example.com",
              specialRequests: "Champagne on arrival",
              services: ["Groceries", "Private Chef"], budgetUSD: 750,
              inStockDate: "2025-09-02T16:00:00Z",
              status: "active",
              hostLink: "https://airbnb-luxe.link/order/1001/host",
              guestLink: "https://airbnb-luxe.link/order/1001/guest",
              createdAt: "2025-08-20T12:00:00Z",
            },
            {
              id: "ord_1002", guestName: "David Kim",
              address: "786 Marine Ave, Newport Beach, CA",
              hostName: "Alexander Ali", hostContact: "+1 310 555 1212",
              otaHostEmail: "host@example.com", guestEmail: "guest@example.com",
              specialRequests: "Yoga session at 8am",
              services: ["Activities"], budgetUSD: 300,
              inStockDate: "2025-09-10T16:00:00Z",
              status: "awaiting_info",
              hostLink: "https://airbnb-luxe.link/order/1002/host",
              guestLink: "https://airbnb-luxe.link/order/1002/guest",
              createdAt: "2025-08-21T08:30:00Z",
            },
            {
              id: "ord_1003", guestName: "Maria Gonzalez",
              address: "345 Jasmine, Laguna Beach, CA",
              hostName: "Alexander Ali", hostContact: "+1 310 555 1212",
              otaHostEmail: "host@example.com", guestEmail: "guest@example.com",
              specialRequests: "",
              services: ["Transport", "Dining"], budgetUSD: 450,
              inStockDate: "2025-08-15T16:00:00Z",
              status: "completed",
              hostLink: "https://airbnb-luxe.link/order/1003/host",
              guestLink: "https://airbnb-luxe.link/order/1003/guest",
              createdAt: "2025-08-10T09:45:00Z",
            },
          ]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Filters
  const filtered = useMemo(() => {
    return orders
      .filter((o) => (statusTab === "all" ? true : mapStatusToUi(o.status) === statusTab))
      .filter((o) =>
        query.trim()
          ? [
              o.guestName,
              o.address,
              o.hostName,
              o.hostContact,
              o.otaHostEmail,
              o.guestEmail,
            ].filter(Boolean).join(" ").toLowerCase().includes(query.toLowerCase())
          : true
      )
      .filter((o) => (serviceFilter === "all" ? true : (o.services || []).includes(serviceFilter)))
      .filter((o) => {
        if (!dateRange.from && !dateRange.to) return true;
        const t = o.inStockDate ? new Date(o.inStockDate).getTime() : NaN;
        const fromOk = dateRange.from ? t >= new Date(dateRange.from).getTime() : true;
        const toOk   = dateRange.to   ? t <= new Date(dateRange.to).getTime()   : true;
        return isFinite(t) ? fromOk && toOk : true;
      });
  }, [orders, statusTab, query, serviceFilter, dateRange]);

  const kpi = useMemo(() => {
    const total = orders.length;
    const active    = orders.filter((x) => mapStatusToUi(x.status) === "active").length;
    const awaiting  = orders.filter((x) => mapStatusToUi(x.status) === "awaiting_info").length;
    const completed = orders.filter((x) => mapStatusToUi(x.status) === "completed").length;
    return { total, active, awaiting, completed };
  }, [orders]);

  // Actions
  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast("Link copied");
      setTimeout(() => setToast(""), 1200);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch {}
  };

  const openDetails = (order) => {
    setDrawerOrder(order);
    setDrawerOpen(true);
  };

  const handleExport = async () => {
    try {
      const res = await request.get("/ota/orders/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "airbnb-luxe-orders.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      const header = [
        "id","guestName","address","hostName","hostContact","otaHostEmail","guestEmail",
        "services","budgetUSD","inStockDate","status","hostLink","guestLink"
      ].join(",");
      const rows   = filtered.map((o) => [
        o.id, `"${o.guestName||""}"`, `"${o.address||""}"`, `"${o.hostName||""}"`, `"${o.hostContact||""}"`,
        `"${o.otaHostEmail||""}"`, `"${o.guestEmail||""}"`,
        `"${(o.services || []).join("|")}"`, o.budgetUSD || "",
        o.inStockDate || "", mapStatusToUi(o.status), o.hostLink || "", o.guestLink || "",
      ].join(",")).join("\n");
      const csv = [header, rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "airbnb-luxe-orders.csv"; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const toggleService = (name) =>
    setForm((p) => ({ ...p, services: p.services.includes(name) ? p.services.filter((s) => s !== name) : [...p.services, name] }));

  const createOrderLink = async () => {
    if (!form.guestName || !form.address || !form.arrivalDate) return;
    setCreating(true);
    try {
      const inStockDate = new Date(`${form.arrivalDate}T${form.arrivalTime || "16:00"}:00`);
      const payload = {
        source: "airbnb_luxe",
        propertyId: DEFAULT_PROPERTY_ID,
        otaHostEmail: form.otaHostEmail,
        otaHostPhone: form.hostContact,
        guestName: form.guestName,
        guestEmail: form.guestEmail,
        address: { raw: form.address },
        inStockDate,
        budgetUSD: form.budgetUSD ? Number(form.budgetUSD) : undefined,
        guestNotes: form.notes || "",
        tags: form.tags || [],
      };

      const { data } = await request.post("/ota/orders", payload);

      const created = {
        id: data.order._id,
        guestName: data.order.guestName,
        address: form.address,
        hostName: form.hostName,
        hostContact: form.hostContact,
        otaHostEmail: form.otaHostEmail,
        guestEmail: form.guestEmail,
        budgetUSD: form.budgetUSD,
        status: data.order.status,           // "created"
        inStockDate: data.order.inStockDate,
        hostLink: data.links.hostCartLink,
        guestLink: data.links.guestCartLink,
        createdAt: data.order.createdAt
      };

      setOrders((prev) => [created, ...prev]);
      setCreateOpen(false);
      setForm({
        guestName: "", guestEmail: "", address: "", arrivalDate: "", arrivalTime: "",
        hostName: "", hostContact: "", otaHostEmail: "",
        specialRequests: "", services: [], budgetUSD: "", notes: "", tags: []
      });

      // default: copy guest link
      setToast("Guest link copied");
      if (created.guestLink) navigator.clipboard.writeText(created.guestLink).catch(() => {});
      setTimeout(() => setToast(""), 1400);
    } finally {
      setCreating(false);
    }
  };

  /* ------------- Render ------------- */
  return (
    <div className="flex flex-col min-h-screen w-full overflow-x-hidden" style={{ backgroundColor: COLORS.bg }}>
      {/* Header */}
      <div className="w-full flex items-center justify-between px-8 py-5"
           style={{ backgroundColor: COLORS.surface, borderBottom: `1px solid ${COLORS.border}` }}>
        <div className="flex items-center w-full">
          <img
            src="https://bellhopt-public.s3.us-west-1.amazonaws.com/airbnbluxe.png"
            alt="Airbnb Luxe"
            className="h-8 mr-3"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const fallback = document.getElementById("luxe-fallback");
              if (fallback) fallback.style.display = "block";
            }}
          />
          <div id="luxe-fallback" style={{ display: "none" }} className="text-base font-semibold">Airbnb Luxe</div>

          <div className="ml-4 flex items-center rounded-full px-3 grow max-w-[520px]"
               style={{ border: `1px solid ${COLORS.border}`, backgroundColor: "#FFFFFF" }}>
            <Search className="w-4 h-4 mr-2" style={{ color: COLORS.subtext }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guest, address, host"
              className="outline-none text-sm py-2 w-full"
              style={{ color: COLORS.text, backgroundColor: "#FFFFFF" }}
            />
          </div>
        </div>

        <div className="flex items-center space-x-3 ml-4 shrink-0">
          <button onClick={handleExport} className="px-4 py-2 rounded-full border flex items-center"
                  style={{ backgroundColor: "#FFFFFF", borderColor: COLORS.border, color: COLORS.text }}>
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </button>
          <button onClick={() => setCreateOpen(true)} className="px-5 py-2.5 rounded-full flex items-center font-medium"
                  style={{ backgroundColor: COLORS.accent, color: "#FFFFFF" }}>
            <PlusCircle className="w-5 h-5 mr-2" /> Create Order Link
          </button>
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, #FFE4EA 0%, #FFF4F7 100%)", color: COLORS.accent }} title="Concierge">
            <BadgeCheck className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Scrollable frame */}
      <div className="flex-1 min-h-0 flex flex-col w-full">
        {/* Hero */}
        <HeroBanner />

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-8 py-6 w-full">
          <KpiCard icon={<Sparkles />}    label="Active"        value={kpi.active}    accent="#22C55E" />
          <KpiCard icon={<AlertCircle />} label="Awaiting Info" value={kpi.awaiting}  accent="#F59E0B" />
          <KpiCard icon={<CheckCircle2 />}label="Completed"     value={kpi.completed} accent="#3B82F6" />
          <KpiCard icon={<Link2 />}       label="Total Orders"  value={kpi.total}     accent={COLORS.accent} />
        </div>

        {/* Controls */}
        <div className="px-8 pb-4 w-full">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
            {/* Tabs */}
            <div className="flex items-center flex-wrap">
              {["active", "awaiting_info", "scheduled", "completed", "archived", "all"].map((t) => (
                <button
                  key={t}
                  onClick={() => setStatusTab(t)}
                  className={cn("px-3 py-2 rounded-full text-sm mr-2 mb-2", statusTab === t ? "font-semibold" : "font-medium")}
                  style={{
                    backgroundColor: statusTab === t ? "#FFE4E8" : "#FFFFFF",
                    color: statusTab === t ? COLORS.accent : COLORS.text,
                    border: `1px solid ${COLORS.border}`,
                  }}
                >
                  {t === "awaiting_info" ? "Awaiting Info" : t[0].toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-3">
              <div className="hidden md:flex items-center">
                <Filter className="w-4 h-4 mr-2" style={{ color: COLORS.subtext }} />
                <select
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  className="rounded-full px-3 py-2 text-sm"
                  style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text, backgroundColor: "#FFFFFF" }}
                >
                  <option value="all">All services</option>
                  <option value="Groceries">Groceries</option>
                  <option value="Transport">Transport</option>
                  <option value="Dining">Dining</option>
                  <option value="Private Chef">Private Chef</option>
                  <option value="Activities">Activities</option>
                </select>
              </div>

              <input type="date" value={dateRange.from}
                     onChange={(e) => setDateRange((p) => ({ ...p, from: e.target.value }))}
                     className="rounded-full px-3 py-2 text-sm"
                     style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text, backgroundColor: "#FFFFFF" }} />
              <input type="date" value={dateRange.to}
                     onChange={(e) => setDateRange((p) => ({ ...p, to: e.target.value }))}
                     className="rounded-full px-3 py-2 text-sm"
                     style={{ border: `1px solid ${COLORS.border}`, color: COLORS.text, backgroundColor: "#FFFFFF" }} />

              {/* View switch */}
              <div className="ml-2 rounded-full overflow-hidden flex" style={{ border: `1px solid ${COLORS.border}` }}>
                <button onClick={() => setView("cards")}
                        className={cn("px-3 py-2 text-sm", view === "cards" && "font-semibold")}
                        style={{ color: view === "cards" ? COLORS.accent : COLORS.text, backgroundColor: view === "cards" ? "#FFE4E8" : "#FFFFFF" }}>
                  Cards
                </button>
                <button onClick={() => setView("table")}
                        className={cn("px-3 py-2 text-sm", view === "table" && "font-semibold")}
                        style={{ color: view === "table" ? COLORS.accent : COLORS.text, backgroundColor: view === "table" ? "#FFE4E8" : "#FFFFFF" }}>
                  Table
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-8 pb-10 w-full">
          <AnimatePresence mode="wait">
            {view === "cards" ? (
              <motion.div key="cards" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                          className="grid gap-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                {loading
                  ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)
                  : filtered.length === 0
                  ? <EmptyState onCreate={() => setCreateOpen(true)} />
                  : filtered.map((o, i) => (
                      <OrderCard
                        key={o.id}
                        order={o}
                        thumbSources={[THUMBNAILS[i % THUMBNAILS.length]]}
                        copiedId={copiedId}
                        onCopy={() => o.guestLink && copy(o.guestLink, o.id)}
                        onOpen={() => openDetails(o)}
                      />
                    ))}
              </motion.div>
            ) : (
              <motion.div key="table" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
                <OrdersTable
                  loading={loading}
                  rows={filtered}
                  copiedId={copiedId}
                  onCopy={(id, _unused) => {
                    const row = filtered.find(r => r.id === id);
                    if (row?.guestLink) copy(row.guestLink, id);
                  }}
                  onOpen={openDetails}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Create Modal (scrollable) */}
      {createOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.45)" }}>
          <motion.div initial={{ opacity: 0, scale: 0.98, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.98, y: 8 }}
            className="w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden max-h-[85vh] flex flex-col"
            style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${COLORS.border}` }}>
              <h3 className="text-lg font-semibold" style={{ color: COLORS.text }}>Create Order Link</h3>
              <button onClick={() => setCreateOpen(false)}><X className="w-5 h-5" style={{ color: COLORS.subtext }} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Guest Name" value={form.guestName} onChange={(v) => setForm((p) => ({ ...p, guestName: v }))} />
              <Input label="Guest Email" value={form.guestEmail} onChange={(v) => setForm((p) => ({ ...p, guestEmail: v }))} />
              <Input label="Host Name" value={form.hostName} onChange={(v) => setForm((p) => ({ ...p, hostName: v }))} />
              <Input label="Host Contact (Phone)" value={form.hostContact} onChange={(v) => setForm((p) => ({ ...p, hostContact: v }))} />
              <Input label="Host Email" value={form.otaHostEmail} onChange={(v) => setForm((p) => ({ ...p, otaHostEmail: v }))} />
              <Input label="Property Address" value={form.address} onChange={(v) => setForm((p) => ({ ...p, address: v }))} className="md:col-span-2" />
              <Input label="Guest Arrival Date"  type="date" value={form.arrivalDate}  onChange={(v) => setForm((p) => ({ ...p, arrivalDate: v }))} />
              <Input label="Arrival Time" type="time" value={form.arrivalTime} onChange={(v) => setForm((p) => ({ ...p, arrivalTime: v }))} />
              <Textarea label="Special Requests" value={form.specialRequests} onChange={(v) => setForm((p) => ({ ...p, specialRequests: v }))} className="md:col-span-2" />

              <div className="md:col-span-2">
                <label className="text-sm font-medium block mb-2" style={{ color: COLORS.text }}>Services Needed</label>
                <div className="flex flex-wrap">
                  {["Groceries", "Transport", "Dining", "Private Chef", "Activities"].map((s) => {
                    const active = form.services.includes(s);
                    return (
                      <button key={s} onClick={() => toggleService(s)} className="px-3 py-2 rounded-full text-sm mr-2 mb-2"
                        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: active ? "#FFE4E8" : "#FFFFFF", color: active ? COLORS.accent : COLORS.text }}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Input label="Budget (USD)" type="number" value={form.budgetUSD} onChange={(v) => setForm((p) => ({ ...p, budgetUSD: v }))} />
              <Textarea label="Concierge Notes" value={form.notes} onChange={(v) => setForm((p) => ({ ...p, notes: v }))} className="md:col-span-2" />
            </div>

            <div className="flex items-center justify-between px-6 py-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
              <div className="text-sm" style={{ color: COLORS.subtext }}>Two links are created on submit (Host + Guest). Guest link will be auto-copied.</div>
              <div className="flex items-center space-x-3">
                <button onClick={() => setCreateOpen(false)} className="px-4 py-2 rounded-full"
                        style={{ backgroundColor: "#FFFFFF", color: COLORS.text, border: `1px solid ${COLORS.border}` }}>
                  Cancel
                </button>
                <button onClick={createOrderLink} disabled={creating || !form.guestName || !form.address || !form.arrivalDate}
                        className="px-5 py-2.5 rounded-full font-medium"
                        style={{ backgroundColor: COLORS.accent, color: "#FFFFFF", opacity: creating ? 0.7 : 1 }}>
                  {creating ? "Creating" : "Create Link"}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Details Drawer */}
      {drawerOpen && drawerOrder && (
        <div className="fixed inset-0 z-30 flex">
          <div className="flex-1" onClick={() => setDrawerOpen(false)} style={{ backgroundColor: "rgba(0,0,0,0.25)" }} />
          <motion.div initial={{ x: 520, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 520, opacity: 0 }}
            className="w-full max-w-[520px] h-full overflow-hidden shadow-2xl rounded-l-3xl flex flex-col"
            style={{ backgroundColor: COLORS.surface }}>
            {/* Hero */}
            <div className="relative">
              <SmartImage sources={THUMBNAILS} alt="Property" className="w-full h-[140px] object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.25) 100%)" }} />
              <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                <div className="text-white">
                  <div className="text-xs opacity-90">Order</div>
                  <div className="text-lg font-semibold">{drawerOrder.guestName}</div>
                </div>
                <StatusChip status={mapStatusToUi(drawerOrder.status)} compact />
              </div>
              <button onClick={() => setDrawerOpen(false)} className="absolute top-3 right-3 rounded-full p-1.5"
                      style={{ backgroundColor: "rgba(255,255,255,0.85)" }}>
                <X className="w-5 h-5" style={{ color: COLORS.subtext }} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <Section title="Details">
                <InfoRow icon={<Home className="w-4 h-4" style={{ color: COLORS.subtext }} />} label="Property" value={drawerOrder.address} />
                <InfoRow icon={<User className="w-4 h-4" style={{ color: COLORS.subtext }} />} label="Host"     value={drawerOrder.hostName} />
                <InfoRow icon={<Phone className="w-4 h-4" style={{ color: COLORS.subtext }} />} label="Contact"  value={drawerOrder.hostContact} />
                <InfoRow icon={<Calendar className="w-4 h-4" style={{ color: COLORS.subtext }} />} label="Arrival"
                         value={drawerOrder.inStockDate ? new Date(drawerOrder.inStockDate).toLocaleString() : "—"} />
              </Section>

              <Section title="Requests">
                <InfoRow icon={<FileText className="w-4 h-4" style={{ color: COLORS.subtext }} />} label="Requests"
                         value={drawerOrder.specialRequests || "None"} />
                <InfoRow icon={<AlertCircle className="w-4 h-4" style={{ color: COLORS.subtext }} />} label="Services"
                         value={(drawerOrder.services || []).length ? drawerOrder.services.join(", ") : "None"} />
              </Section>

              <Section title="Budget & Notes">
                <InfoRow icon={<CheckCircle2 className="w-4 h-4" style={{ color: COLORS.subtext }} />} label="Budget"
                         value={drawerOrder.budgetUSD ? `$${drawerOrder.budgetUSD}` : "None"} />
                <div>
                  <div className="text-xs mb-1" style={{ color: COLORS.subtext }}>Concierge Notes</div>
                  <div className="rounded-xl p-3" style={{ backgroundColor: COLORS.bg, color: COLORS.text, border: `1px solid ${COLORS.border}` }}>
                    {drawerOrder.notes || "No notes"}
                  </div>
                </div>
              </Section>
            </div>

            {/* Sticky footer actions */}
            <div className="px-6 py-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
              <div className="flex items-center justify-between">
                <div className="text-xs" style={{ color: COLORS.subtext }}>
                  Created {new Date(drawerOrder.createdAt || Date.now()).toLocaleDateString()}
                </div>
                <div className="flex items-center space-x-2">
                  {drawerOrder.hostLink || drawerOrder.guestLink ? (
                    <>
                      {drawerOrder.hostLink && (
                        <a href={drawerOrder.hostLink} target="_blank" rel="noreferrer"
                           className="px-3 py-1.5 rounded-full text-sm"
                           style={{ backgroundColor: "#FAFAFA", color: COLORS.text, border: `1px solid ${COLORS.border}` }}>
                          Open Host Link
                        </a>
                      )}
                      {drawerOrder.guestLink && (
                        <button onClick={() => copy(drawerOrder.guestLink, drawerOrder.id)}
                                className="px-3 py-1.5 rounded-full text-sm"
                                style={{ backgroundColor: "#FFE4E8", color: COLORS.accent }}>
                          Copy Guest Link
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="text-sm" style={{ color: COLORS.subtext }}>No links</span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Toast */}
      <Toast show={!!toast}>{toast}</Toast>
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function HeroBanner() {
  return (
    <div className="px-8 pt-6 w-full">
      <div className="relative rounded-3xl overflow-hidden w-full">
        <SmartImage sources={HERO_IMAGES} alt="Luxe retreat" className="w-full h-[200px] md:h-[260px] object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.35) 50%, rgba(0,0,0,0.30) 100%)" }} />
        <div className="absolute inset-0 flex flex-col justify-center px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center mb-3 px-3 py-1 rounded-full text-xs font-medium"
                 style={{ backgroundColor: "rgba(255,255,255,0.85)", color: COLORS.text }}>
              <Sparkles className="w-4 h-4 mr-1.5" />
              Concierge Hub for every stay
            </div>
            <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: "#FFFFFF", textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
              Create guest order links, track requests, and deliver five star experiences
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, icon }) {
  return (
    <div className="rounded-2xl p-5 flex items-center justify-between shadow w-full"
         style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <div>
        <div className="text-sm mb-1" style={{ color: COLORS.subtext }}>{label}</div>
        <div className="text-2xl font-semibold" style={{ color: COLORS.text }}>{value}</div>
      </div>
      <div className="w-11 h-11 rounded-2xl flex items-center justify-center"
           style={{ backgroundColor: "#FAFAFA", border: `1px solid ${COLORS.border}`, color: accent }}>
        {icon}
      </div>
    </div>
  );
}

function OrderCard({ order, thumbSources, copiedId, onCopy, onOpen }) {
  return (
    <motion.div whileHover={{ y: -2 }} className="rounded-3xl overflow-hidden shadow w-full"
                style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <div className="relative">
        <SmartImage sources={thumbSources} alt="Property" className="w-full h-40 object-cover" />
        <div className="absolute top-3 left-3">
          <StatusChip status={mapStatusToUi(order.status)} />
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0">
            <div className="w-10 h-10 rounded-full mr-3 flex items-center justify-center"
                 style={{ backgroundColor: "#F9F3F4", color: COLORS.accent, border: `1px solid ${COLORS.border}` }}>
              <span className="text-sm font-semibold">{initials(order.guestName)}</span>
            </div>
            <div className="min-w-0">
              <div className="text-base font-semibold truncate" style={{ color: COLORS.text }}>{order.guestName}</div>
              <div className="text-xs truncate" style={{ color: COLORS.subtext }}>
                {order.hostName} • {order.hostContact}
              </div>
            </div>
          </div>

          <button onClick={onOpen} className="px-3 py-1.5 rounded-full text-xs"
                  style={{ backgroundColor: "#FFE4E8", color: COLORS.accent }}>
            View
          </button>
        </div>

        <div className="mt-3 text-sm flex items-start" style={{ color: COLORS.text }}>
          <Home className="w-4 h-4 mr-2" style={{ color: COLORS.subtext }} />
          <span className="truncate">{order.address}</span>
        </div>

        <div className="mt-2 text-sm flex items-center" style={{ color: COLORS.text }}>
          <Calendar className="w-4 h-4 mr-2" style={{ color: COLORS.subtext }} />
          {order.inStockDate ? new Date(order.inStockDate).toLocaleString() : ""}
        </div>

        <div className="mt-3 flex flex-wrap">
          {(order.services || []).slice(0, 3).map((s) => (
            <span key={s} className="px-2.5 py-1 rounded-full text-xs mr-2 mb-2"
                  style={{ backgroundColor: "#FAFAFA", color: COLORS.text, border: `1px solid ${COLORS.border}` }}>
              {s}
            </span>
          ))}
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm" style={{ color: COLORS.subtext }}>
            Budget {order.budgetUSD ? `$${order.budgetUSD}` : "None"}
          </div>
          <div className="flex items-center">
            {order.hostLink || order.guestLink ? (
              <>
                {order.hostLink && (
                  <a href={order.hostLink} target="_blank" rel="noreferrer" className="inline-flex items-center mr-3"
                     style={{ color: COLORS.accent }}>
                    <ExternalLink className="w-4 h-4 mr-1" /> Open
                  </a>
                )}
                {order.guestLink && (
                  <button onClick={onCopy} className="inline-flex items-center" style={{ color: COLORS.text }}>
                    {copiedId === order.id ? (<><Check className="w-4 h-4 mr-1" /> Copied</>) : (<><Copy className="w-4 h-4 mr-1" /> Copy</>)}
                  </button>
                )}
              </>
            ) : (
              <span style={{ color: COLORS.subtext }}>No links</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function OrdersTable({ loading, rows, copiedId, onCopy, onOpen }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow w-full"
         style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <table className="min-w-full table-fixed">
        <colgroup>
          <col className="w-[14%]" />
          <col className="w-[22%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[14%]" />
          <col className="w-[10%]" />
          <col className="w-[8%]" />
          <col className="w-[8%]" />
        </colgroup>
        <thead>
          <tr className="text-left text-sm" style={{ color: COLORS.subtext, backgroundColor: "#FCFCFC" }}>
            {["Guest","Property","Host","Contact","Arrival","Services","Budget","Status","Link","Actions"].map((h) => (
              <th key={h} className="px-5 py-3 font-medium truncate">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={10} className="px-5 py-10 text-center" style={{ color: COLORS.subtext }}>Loading</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={10} className="px-5 py-10 text-center" style={{ color: COLORS.subtext }}>No results</td></tr>
          ) : (
            rows.map((o) => (
              <tr key={o.id} className="text-sm" style={{ color: COLORS.text }}>
                <td className="px-5 py-4 font-semibold truncate">{o.guestName}</td>
                <td className="px-5 py-4 truncate">{o.address}</td>
                <td className="px-5 py-4 truncate">{o.hostName}</td>
                <td className="px-5 py-4 truncate">{o.hostContact}</td>
                <td className="px-5 py-4">
                  <div className="flex items-center truncate">
                    <Calendar className="w-4 h-4 mr-2 shrink-0" style={{ color: COLORS.subtext }} />
                    <span className="truncate">{o.inStockDate ? new Date(o.inStockDate).toLocaleString() : ""}</span>
                  </div>
                </td>
                <td className="px-5 py-4 truncate">
                  {(o.services || []).length ? o.services.join(", ") : <span style={{ color: COLORS.subtext }}>None</span>}
                </td>
                <td className="px-5 py-4 truncate">{o.budgetUSD ? `$${o.budgetUSD}` : "None"}</td>
                <td className="px-5 py-4"><StatusChip status={mapStatusToUi(o.status)} compact /></td>
                <td className="px-5 py-4">
                  {o.hostLink || o.guestLink ? (
                    <div className="flex items-center">
                      {o.hostLink && (
                        <a href={o.hostLink} target="_blank" rel="noreferrer" className="inline-flex items-center mr-3"
                           style={{ color: COLORS.accent }}>
                          <ExternalLink className="w-4 h-4 mr-1" /> Open
                        </a>
                      )}
                      {o.guestLink && (
                        <button onClick={() => onCopy(o.id)} className="inline-flex items-center" style={{ color: COLORS.text }}>
                          {copiedId === o.id ? (<><Check className="w-4 h-4 mr-1" /> Copied</>) : (<><Copy className="w-4 h-4 mr-1" /> Copy</>)}
                        </button>
                      )}
                    </div>
                  ) : <span style={{ color: COLORS.subtext }}>No links</span>}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center space-x-3">
                    <button className="px-3 py-1 rounded-full text-xs"
                            style={{ backgroundColor: "#FFE4E8", color: COLORS.accent }}
                            onClick={() => onOpen(o)}>
                      View
                    </button>
                    <button className="p-1 rounded-full" style={{ backgroundColor: COLORS.muted, color: "#3F3F46" }} title="More">
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-3xl overflow-hidden shadow w-full"
         style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <div className="w-full h-40" style={{ backgroundColor: COLORS.muted }} />
      <div className="p-5 space-y-3">
        <div className="w-1/2 h-4" style={{ backgroundColor: COLORS.muted }} />
        <div className="w-3/4 h-3" style={{ backgroundColor: COLORS.muted }} />
        <div className="w-2/3 h-3" style={{ backgroundColor: COLORS.muted }} />
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="col-span-full rounded-3xl p-10 text-center w-full"
         style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
      <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4"
           style={{ background: "linear-gradient(135deg,#FFE4EA,#FFF5F7)", color: COLORS.accent }}>
        <Sparkles className="w-7 h-7" />
      </div>
      <div className="text-lg font-semibold mb-1" style={{ color: COLORS.text }}>No orders match your filters</div>
      <div className="text-sm mb-4" style={{ color: COLORS.subtext }}>Adjust filters or create a new order link</div>
      <button onClick={onCreate} className="inline-flex items-center px-4 py-2 rounded-full"
              style={{ backgroundColor: COLORS.accent, color: "#FFFFFF" }}>
        <PlusCircle className="w-4 h-4 mr-2" /> Create Order Link
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-2xl p-4" style={{ backgroundColor: "#FFFFFF", border: `1px solid ${COLORS.border}` }}>
      <div className="text-sm font-semibold mb-3" style={{ color: COLORS.text }}>{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium block mb-2" style={{ color: COLORS.text }}>{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl px-3 py-2 outline-none"
        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: "#FFFFFF", color: COLORS.text, boxShadow: "0 0 0 0 rgba(0,0,0,0)" }}
        onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 4px ${COLORS.focus}`)}
        onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
      />
    </div>
  );
}

function Textarea({ label, value, onChange, className = "" }) {
  return (
    <div className={className}>
      <label className="text-sm font-medium block mb-2" style={{ color: COLORS.text }}>{label}</label>
      <textarea
        rows={4} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl px-3 py-2 outline-none"
        style={{ border: `1px solid ${COLORS.border}`, backgroundColor: "#FFFFFF", color: COLORS.text }}
        onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 4px ${COLORS.focus}`)}
        onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
      />
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start">
      <div className="mt-0.5 mr-3">{icon}</div>
      <div>
        <div className="text-xs mb-1" style={{ color: COLORS.subtext }}>{label}</div>
        <div className="text-sm" style={{ color: COLORS.text }}>{value}</div>
      </div>
    </div>
  );
}

function mapApiOrderToUi(o) {
  // shape orders from GET /api/v1/ota/orders if you add it
  return {
    id: o._id || o.id,
    guestName: o.guestName,
    guestEmail: o.guestEmail,
    address: o.address?.raw || o.address,
    hostName: o.hostName,           // optional UI-only
    hostContact: o.otaHostPhone,
    otaHostEmail: o.otaHostEmail,
    budgetUSD: o.budgetUSD,
    status: o.status,
    inStockDate: o.inStockDate,
    services: o.services || [],
    hostLink: o.hostCartLink,
    guestLink: o.guestCartLink,
    createdAt: o.createdAt
  };
}

export default ConciergeDashboard;
